// Optimistic-bubble bookkeeping for the bot/AI answer send path — #146330.
//
// The widget pushes the customer's bubble into the timeline BEFORE the
// `/workflow/answer` POST resolves. Three decisions around that bubble used to
// live inline in the ticket store and were wrong in the same way: a failed or
// timed-out POST (typical for the first send after a long idle) was treated as
// success, and the next silent refetch (tab refocus, widget reopen) replaced
// the timeline wholesale — so the never-persisted bubble simply vanished.
//
// Kept free of store imports so it can be unit tested under CRA jest.

import type { TicketChatMessage } from "../Stores/Ticket";

// Comment types authored by the customer (the server-side echo of a local
// optimistic bubble is always one of these).
export const CUSTOMER_MESSAGE_TYPES = [
  "BOT_REPLY",
  "SHARED_COMMENT",
  "BOT_INPUT_REPLY",
  "USER_TEXT",
];

export const isCustomerAuthored = (comment?: TicketChatMessage): boolean =>
  !!comment && CUSTOMER_MESSAGE_TYPES.includes(comment.type ?? "");

export type AnswerSendErrorReason = "timeout" | "network" | "closed" | "failed";

// `HttpClientXHR` rejects with `{ status, statusText, data }`; status 0 is a
// network error or a client-side timeout (statusText "Timeout"). 409 is what
// the API answers for a conversation that can no longer take replies.
export const classifyAnswerSendError = (err: any): AnswerSendErrorReason => {
  const status = Number(err?.status ?? 0);
  if (!status) {
    return err?.statusText === "Timeout" ? "timeout" : "network";
  }
  if (status === 409) {
    return "closed";
  }
  return "failed";
};

export interface AnswerSendOutcome {
  optimisticId: string;
  success: boolean;
  serverTime?: Date;
  attachments?: any[];
  errorMessage?: string;
}

// Patch ONLY the bubble this send owns. `sendComment`'s error path sweeps
// every `sending` comment; here a second send may already be in flight and
// must keep its own state.
export const applyAnswerSendOutcome = (
  comments: TicketChatMessage[],
  outcome: AnswerSendOutcome,
): TicketChatMessage[] =>
  comments.map((comment) => {
    if (comment.id !== outcome.optimisticId) {
      return comment;
    }
    if (outcome.success) {
      const time = outcome.serverTime ?? comment.createdAt;
      return {
        ...comment,
        createdAt: time,
        updatedAt: time,
        attachments: outcome.attachments ?? comment.attachments ?? [],
        sending: false,
        error: false,
        errorMessage: undefined,
      };
    }
    return {
      ...comment,
      sending: false,
      error: true,
      errorMessage: outcome.errorMessage,
    };
  });

// How far the server copy of a message may predate the local bubble and still
// count as the same message. The local time is seeded from the last comment
// (+1ms) rather than the wall clock, so it can lag or lead the server's.
const SAME_MESSAGE_WINDOW_MS = 60_000;

const toTime = (value: any): number => {
  const t = new Date(value ?? 0).getTime();
  return Number.isFinite(t) ? t : 0;
};

const normalizeContent = (comment: TicketChatMessage): string =>
  String(comment?.data?.content ?? "").trim();

// True when the fetched snapshot already holds the server-side copy of a
// local optimistic bubble (a timed-out POST that the server processed anyway).
const snapshotContainsLocal = (
  local: TicketChatMessage,
  fetched: TicketChatMessage[],
): boolean => {
  const content = normalizeContent(local);
  if (!content) return false;
  const localTime = toTime(local.createdAt);
  return fetched.some(
    (candidate) =>
      isCustomerAuthored(candidate) &&
      normalizeContent(candidate) === content &&
      toTime(candidate.createdAt) >= localTime - SAME_MESSAGE_WINDOW_MS,
  );
};

// Merge a wholesale server snapshot with the local timeline: the snapshot is
// authoritative for everything the server knows about, and local bubbles that
// are still in flight or failed to send are carried over — they exist nowhere
// else, and dropping them is exactly the "my message disappeared" bug.
export const mergeRefetchedComments = (
  local: TicketChatMessage[],
  fetched: TicketChatMessage[],
): TicketChatMessage[] => {
  const snapshot = fetched ?? [];
  const fetchedIds = new Set(snapshot.map((c) => c.id));
  const carried = (local ?? []).filter(
    (comment) =>
      (comment.sending || comment.error) &&
      !fetchedIds.has(comment.id) &&
      !snapshotContainsLocal(comment, snapshot),
  );
  return carried.length > 0 ? [...snapshot, ...carried] : snapshot;
};

// A freshly created comment arriving over WS / findOne: only a customer-
// authored one is the echo of a local optimistic bubble, so only then may the
// still-`sending` locals be dropped. A bot or agent message landing while the
// customer's POST is still in flight must leave that bubble alone. Errored
// bubbles are never dropped here — the customer clears them by resending.
export const dropSendingLocalsFor = (
  local: TicketChatMessage[],
  created: TicketChatMessage,
): TicketChatMessage[] => {
  if (!isCustomerAuthored(created)) {
    return local;
  }
  return local.filter((comment) => !comment.sending);
};
